import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, ArrowUpRight, ArrowDownLeft, IndianRupee, Wallet as WalletIcon, Info,
  Banknote, ChevronRight,
} from "lucide-react";

const QUICK = [100, 250, 500, 1000, 2000];
const WD_STATUS_STYLE = {
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  approved: "bg-brand-green/10 text-brand-green border-brand-green/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

/** Load Razorpay checkout script on demand. */
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function WalletPage() {
  const { user, refreshMe } = useAuth();
  const { t } = useI18n();
  const [txs, setTxs] = useState([]);
  const [balance, setBalance] = useState(user?.wallet_balance || 0);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [integration, setIntegration] = useState({ payments: { provider: "mock" } });
  const [withdrawals, setWithdrawals] = useState([]);
  const [wdOpen, setWdOpen] = useState(false);
  const [wdForm, setWdForm] = useState({
    amount: "", bank_account_name: "", bank_account_number: "", bank_ifsc: "", upi_id: "",
  });
  const [wdLoading, setWdLoading] = useState(false);

  const canWithdraw = user?.role === "retailer" || user?.role === "distributor";

  const load = async () => {
    const promises = [
      api.get("/wallet/balance"),
      api.get("/wallet/transactions"),
      api.get("/integrations/status"),
    ];
    if (canWithdraw) promises.push(api.get("/wallet/withdrawals"));
    const [b, t2, i, w] = await Promise.all(promises);
    setBalance(b.data.balance);
    setTxs(t2.data);
    setIntegration(i.data);
    if (w) setWithdrawals(w.data);
  };
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const money = (v) => "₹" + Number(v || 0).toFixed(2);
  const isLive = integration?.payments?.live;

  const submitWithdrawal = async () => {
    const amt = Number(wdForm.amount);
    if (!amt || amt < 100) return toast.error("Minimum withdrawal is ₹100");
    if (amt > balance) return toast.error(`Amount exceeds balance (₹${balance})`);
    if (!wdForm.bank_account_name.trim() || wdForm.bank_account_number.length < 6 || wdForm.bank_ifsc.length < 8)
      return toast.error("Fill bank details (name, account, IFSC)");
    setWdLoading(true);
    try {
      await api.post("/wallet/withdraw", { ...wdForm, amount: amt });
      toast.success(`Withdrawal request submitted for ₹${amt}`);
      setWdOpen(false);
      setWdForm({ amount: "", bank_account_name: "", bank_account_number: "", bank_ifsc: "", upi_id: "" });
      await refreshMe();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setWdLoading(false);
    }
  };

  const recharge = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt < 1) return toast.error("Minimum recharge is ₹1");
    setLoading(true);
    try {
      const { data: order } = await api.post("/wallet/create-order", { amount: amt });

      // Live Razorpay flow (always, when keys are configured).
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Failed to load Razorpay. Check your network.");
        return;
      }
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: "MaaPrint",
        description: "Wallet recharge",
        prefill: { name: user.name, email: user.email, contact: user.phone || "" },
        theme: { color: "#0F62FE" },
        handler: async (res) => {
          try {
            const { data } = await api.post("/wallet/verify", {
              order_id: res.razorpay_order_id,
              payment_id: res.razorpay_payment_id,
              signature: res.razorpay_signature,
            });
            toast.success(`Recharge successful — ₹${amt} credited`);
            setBalance(data.balance);
            setAmount("");
            setOpen(false);
            await refreshMe();
            load();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            {canWithdraw ? "Earnings" : "Money"}
          </p>
          <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">
            {canWithdraw ? "Wallet & Earnings" : t("wallet.title")}
          </h1>
          {canWithdraw && (
            <p className="text-sm text-muted-foreground mt-1">
              Credited automatically from every walk-in customer payment (96% of each order). Withdraw to your bank when you're ready.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 p-8 border-border relative overflow-hidden bg-gradient-to-br from-brand-blue to-brand-green text-white">
          <div className="absolute inset-0 hero-grid opacity-15" />
          <div className="relative">
            <div className="flex items-center gap-2 text-white/80">
              <WalletIcon size={18} />
              <span className="text-xs uppercase tracking-widest">
                {canWithdraw ? "Available to withdraw" : t("wallet.currentBalance")}
              </span>
            </div>
            <p className="mt-4 text-5xl font-display font-bold tabular-nums" data-testid="wallet-balance">
              ₹{Number(balance).toFixed(2)}
            </p>
            <p className="text-white/80 text-sm mt-2">
              {canWithdraw
                ? "Credited automatically from customer QR payments."
                : t("wallet.autoDeduct")}
            </p>
            {/* Recharge UI only for super_admin (manual finance adjustments).
                Retailers earn from customer payments — no top-up needed. */}
            {!canWithdraw && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="mt-6 bg-white text-brand-blue hover:bg-white/90" data-testid="wallet-add-money-btn">
                  <Plus size={16} className="mr-2" /> {t("wallet.addMoney")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display">{t("wallet.recharge")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>{t("common.amount")}</Label>
                    <div className="relative mt-1.5">
                      <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input type="number" min={1} placeholder="500"
                        value={amount} onChange={(e) => setAmount(e.target.value)}
                        className="pl-9" data-testid="wallet-amount-input" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {QUICK.map((q) => (
                      <Button key={q} variant="outline" size="sm"
                        onClick={() => setAmount(String(q))} data-testid={`wallet-quick-${q}`}>
                        ₹{q}
                      </Button>
                    ))}
                  </div>
                  <div className={`rounded-lg p-3 text-xs flex items-start gap-2 ${
                    isLive ? "bg-brand-green/10 text-brand-green" : "bg-amber-500/10 text-amber-700 dark:text-amber-400"}`}>
                    <Info size={14} className="mt-0.5 flex-shrink-0" />
                    <span>
                      {isLive ? "Live Razorpay payment gateway." : t("wallet.razorpayHint")}
                    </span>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                  <Button onClick={recharge} disabled={loading}
                    className="bg-primary hover:bg-brand-blue-hover" data-testid="wallet-recharge-confirm-btn">
                    {loading ? t("wallet.processing") : t("wallet.now")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
            {canWithdraw && (
              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <a href="#withdrawals-section" className="inline-block">
                  <Button className="bg-white text-brand-blue hover:bg-white/90" data-testid="wallet-goto-withdraw-btn">
                    <ArrowUpRight size={16} className="mr-2" /> Withdraw to bank
                  </Button>
                </a>
              </div>
            )}
          </div>
        </Card>

        <Card className="lg:col-span-2 p-6 border-border bg-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">{t("wallet.history")}</h3>
            <Badge variant="outline">{txs.length} {t("wallet.entries")}</Badge>
          </div>
          {txs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="wallet-tx-empty">{t("wallet.empty")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("wallet.description")}</TableHead>
                  <TableHead>{t("common.type")}</TableHead>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead className="text-right">{t("common.amount")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txs.map((t2) => (
                  <TableRow key={t2.id} data-testid={`tx-row-${t2.id}`}>
                    <TableCell className="font-medium max-w-[280px] truncate">{t2.description}</TableCell>
                    <TableCell>
                      {t2.type === "credit" ? (
                        <Badge className="bg-brand-green/10 text-brand-green border border-brand-green/20 hover:bg-brand-green/10">
                          <ArrowDownLeft size={12} className="mr-1" /> {t("wallet.credit")}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-destructive border-destructive/20">
                          <ArrowUpRight size={12} className="mr-1" /> {t("wallet.debit")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{new Date(t2.created_at).toLocaleString()}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${t2.type === "credit" ? "text-brand-green" : "text-foreground"}`}>
                      {t2.type === "credit" ? "+" : "−"}{money(t2.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {/* Withdrawals (retailer / distributor) */}
      {canWithdraw && (
        <Card id="withdrawals-section" className="p-6 border-border bg-card" data-testid="withdrawals-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Payouts</p>
              <h3 className="font-display text-xl font-semibold mt-1">Withdrawals</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Transfer your earnings to your bank / UPI. Requests are reviewed by MaaPrint admin.
              </p>
            </div>
            <Dialog open={wdOpen} onOpenChange={setWdOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-green hover:bg-brand-green-hover" data-testid="wd-request-btn">
                  <Banknote size={15} className="mr-2" /> Request withdrawal
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display">Request withdrawal</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Amount (min ₹100)</Label>
                    <div className="relative mt-1.5">
                      <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input type="number" min={100} value={wdForm.amount}
                        onChange={(e) => setWdForm({ ...wdForm, amount: e.target.value })}
                        className="pl-9" data-testid="wd-amount-input" />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">Available: {money(balance)}</p>
                  </div>
                  <div>
                    <Label>Account holder name</Label>
                    <Input value={wdForm.bank_account_name}
                      onChange={(e) => setWdForm({ ...wdForm, bank_account_name: e.target.value })}
                      className="mt-1.5" data-testid="wd-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Account number</Label>
                      <Input value={wdForm.bank_account_number}
                        onChange={(e) => setWdForm({ ...wdForm, bank_account_number: e.target.value })}
                        className="mt-1.5 font-mono" data-testid="wd-account-input" />
                    </div>
                    <div>
                      <Label>IFSC</Label>
                      <Input value={wdForm.bank_ifsc}
                        onChange={(e) => setWdForm({ ...wdForm, bank_ifsc: e.target.value.toUpperCase() })}
                        className="mt-1.5 font-mono uppercase" data-testid="wd-ifsc-input" />
                    </div>
                  </div>
                  <div>
                    <Label>UPI ID (optional)</Label>
                    <Input value={wdForm.upi_id}
                      onChange={(e) => setWdForm({ ...wdForm, upi_id: e.target.value })}
                      placeholder="you@bank" className="mt-1.5" data-testid="wd-upi-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setWdOpen(false)}>Cancel</Button>
                  <Button onClick={submitWithdrawal} disabled={wdLoading}
                    className="bg-brand-green hover:bg-brand-green-hover" data-testid="wd-submit-btn">
                    {wdLoading ? "Submitting…" : "Submit request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {withdrawals.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="wd-empty">
              No withdrawal requests yet.
            </p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Requested</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>UPI</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {withdrawals.map((w) => (
                  <TableRow key={w.id} data-testid={`wd-row-${w.id}`}>
                    <TableCell className="text-xs">{new Date(w.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs font-mono">
                      {w.bank_account_number} · {w.bank_ifsc}
                    </TableCell>
                    <TableCell className="text-xs">{w.upi_id || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{money(w.amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={WD_STATUS_STYLE[w.status]}>{w.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
